import { ArrowRight, ArrowUpRight, ClipboardList, Layers3, Ruler, SearchCheck } from "lucide-react";
import React from "react";
import { Link } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { resourceLinks } from "@/data/resources";
import { assets, siteConfig } from "@/data/site";

const workingSequence = [
  { icon: SearchCheck, number: "01", title: "Read the room", copy: "Start with facility conditions, moisture, traffic, wall construction, floor, ceiling, fixtures, and accessibility inputs." },
  { icon: Layers3, number: "02", title: "Select the system", copy: "Narrow material, mounting style, finish direction, hardware, accessories, and any replacement-part requirements." },
  { icon: Ruler, number: "03", title: "Document the scope", copy: "Provide plans, dimensions, photos, quantities, delivery location, and unanswered questions in one organized request." },
  { icon: ClipboardList, number: "04", title: "Prepare the quote", copy: "Partition Stalls reviews the submitted information and follows up on gaps that prevent a focused project quotation." },
];

export default function Resources() {
  const url = `${siteConfig.url}/resources`;
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name: "Commercial Restroom Planning Resources",
    description: "Practical commercial restroom planning guides for material selection, budget planning, mounting, measuring, colors and finishes, accessories, and quote preparation.",
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    mainEntity: { "@id": `${url}#guides` },
    breadcrumb: { "@id": `${url}#breadcrumb` },
    primaryImageOfPage: `${siteConfig.url}${assets.planningDrawing}`,
  };
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${url}#guides`,
    name: "Commercial restroom planning guides",
    numberOfItems: resourceLinks.length,
    itemListElement: resourceLinks.map((resource, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: resource.title,
      url: `${siteConfig.url}${resource.href}`,
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "Resources", item: `${siteConfig.url}/resources` },
    ],
  };

  return (
    <>
      <StructuredData data={[collectionSchema, itemListSchema, breadcrumbSchema]} />
      <header className="resource-hero section-line">
        <div className="resource-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span>Resources</p>
          <span className="kicker">Project planning library</span>
          <h1>Commercial restroom resources built around a better quote.</h1>
          <p>Use practical guides for material selection, early budget planning, partition mounting, field dimensions, finishes, accessories, and quote preparation. Each resource connects a project question to the information needed for pricing.</p>
          <Link href="/request-a-quote" className="button button--primary">Start a Quote <ArrowUpRight aria-hidden="true" /></Link>
        </div>
        <figure className="resource-hero__visual technical-figure">
          <img src={assets.planningDrawing} srcSet={`${assets.planningDrawingSmall} 720w, ${assets.planningDrawingMedium} 1200w, ${assets.planningDrawing} 1600w`} sizes="(max-width: 760px) 100vw, 58vw" width="1600" height="873" alt="Technical commercial restroom drawing with partitions, urinal screens, grab bars, mirrors, and dispensers" />
          <figcaption><span>Planning set</span><strong>Partitions + accessories</strong></figcaption>
        </figure>
      </header>

      <section className="resource-index section-line" aria-labelledby="resource-index-title">
        <div className="resource-index__intro"><span className="kicker">Resource index</span><h2 id="resource-index-title">Move from questions to project inputs.</h2><p>These guides are designed for early planning and quote preparation. Project specifications, manufacturer data, and applicable requirements still govern the final selection.</p></div>
        <div className="resource-index__grid">{resourceLinks.map((resource) => <Link href={resource.href} key={resource.href}><span>{resource.number}</span><h3>{resource.title}</h3><p>{resource.copy}</p><strong>Open guide <ArrowRight aria-hidden="true" /></strong></Link>)}</div>
      </section>

      <section className="resource-paths section-line" aria-label="Product planning paths">
        <Link href="/bathroom-partitions"><span>Product path 01</span><h2>Compare all five partition materials.</h2><p>Start with moisture, traffic, appearance, maintenance, and facility type.</p><ArrowRight aria-hidden="true" /></Link>
        <Link href="/commercial-restroom-accessories"><span>Product path 02</span><h2>Build the complete accessory schedule.</h2><p>Organize accessibility, hygiene, paper, waste, compartment, storage, and specialty products.</p><ArrowRight aria-hidden="true" /></Link>
        <Link href="/sub-assemblies"><span>Product path 03</span><h2>Compare hardware sub-assemblies.</h2><p>Use documented comparison boards to prepare a more focused accessory quote.</p><ArrowRight aria-hidden="true" /></Link>
        <Link href="/technical-resources"><span>Product path 04</span><h2>Open the technical resource library.</h2><p>Read Markdown-backed field, accessory, and quote-preparation workflows before sharing project information.</p><ArrowRight aria-hidden="true" /></Link>
      </section>

      <section className="content-section content-section--dark section-line" aria-labelledby="resource-sequence-title">
        <div className="catalog-heading"><div><span className="kicker">A disciplined sequence</span><h2 id="resource-sequence-title">Four steps from field condition to quote request.</h2></div><p>The goal is not to make the buyer solve every detail alone. It is to make the known information clear enough to identify the next decision.</p></div>
        <ol className="resource-sequence">{workingSequence.map(({ icon: Icon, ...step }) => <li key={step.number}><div><span>{step.number}</span><Icon aria-hidden="true" /></div><h3>{step.title}</h3><p>{step.copy}</p></li>)}</ol>
      </section>

      <QuoteBand title="Have plans, dimensions, or an accessory schedule ready?" copy="Send the available project information now. The resource library can help fill in the details, and our team can flag unresolved items that affect pricing." />
    </>
  );
}
